import React, { useState, useEffect } from 'react';

import { Table } from 'react-bootstrap';
import { z } from 'zod';
import styled from 'styled-components';

import Modal from '../utils/ModalComponet';
import CSVImportService from '../../services/CSVImportService';
import BulkUploadButton from '../utils/BulkUploadButton';

const StyledWrapper = styled.div`
  height: 93%;
  width: 100%;

  h3 {
    margin-bottom: 20px;
  }

  .table-responsive {
    margin-bottom: 20px;
    overflow: auto;
    height: 84%;
    display: block;
    background: #fff;
    border: 1px solid #d9d9d9;
    thead {
      position: sticky;
      top: 0;
      z-index: 1000;
    }
  }

  p {
    margin-top: 20px;
  }
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-right: 25px;
`;

type RnrsBulkUploadPreviewProps = {
  isOpen: boolean;
  closeModal: () => void;
  onSend: (data: RnrsData) => void;
  state: {
    error?: string;
  };
};

const rnrsDataSchema = z
  .object({
    productId: z.string().min(1),
    userId: z.string().optional(),
    memberName: z.string().min(1),
    partyType: z.string().optional(),
    review: z.string().optional(),
    rating: z.string().transform(value => parseInt(value)),
    partyStartDate: z.coerce.date().transform(value => value.toISOString()),
    partyLocation: z.string().min(1),
    featured: z.string().transform(value => value.toLowerCase() === 'true'),
  })
  .transform(fields => {
    const { partyType, ...rest } = fields;

    return {
      ...rest,
      partyTypeName: partyType,
    };
  })
  .array();

export type RnrsData = z.infer<typeof rnrsDataSchema>;

const RnrsBulkUploadPreview = ({
  isOpen,
  closeModal,
  onSend,
  state,
}: RnrsBulkUploadPreviewProps) => {
  const [rnrs, setRnrs] = useState<RnrsData | null>(null);
  const [errorAlert, setErrorAlert] = useState('');

  useEffect(() => {
    setRnrs(null);
  }, [isOpen]);

  useEffect(() => {
    if (state.error) setErrorAlert(state.error);
  }, [state]);

  const onBulkUploadChange = async event => {
    try {
      setErrorAlert('');
      const data = await CSVImportService.readFile(event);
      const parsedData = rnrsDataSchema.parse(data);

      setRnrs(parsedData);
    } catch (error) {
      if (error instanceof z.ZodError) {
        setErrorAlert(
          'Invalid data format. Please check the file and try again.'
        );
      }
    }
  };

  return (
    <Modal
      open={isOpen}
      closeModal={closeModal}
      cancel={closeModal}
      action={() => rnrs && onSend(rnrs)}
      actionButtonText='Upload'
      disabled={!rnrs || !rnrs.length}
      stylesProps={{
        width: '85vw',
        height: '90vh',
        maxWidth: 'none',
        background: '#F7F7F7',
      }}
    >
      <StyledWrapper>
        <StyledHeader>
          <h3>Bulk Upload</h3>
          <BulkUploadButton onChange={onBulkUploadChange} label='Load .csv' />
        </StyledHeader>

        <div className='table-responsive'>
          <Table>
            <thead>
              <tr>
                <th>Product Id</th>
                <th>User Id</th>
                <th>Member Name</th>
                <th>Party Type</th>
                <th>Review</th>
                <th>Rating</th>
                <th>Party Start Date</th>
                <th>Party Location</th>
                <th>Featured</th>
              </tr>
            </thead>
            <tbody>
              {errorAlert && (
                <td className='alert alert-danger' role='alert' colSpan={9}>
                  {errorAlert}
                </td>
              )}

              {rnrs?.map((item, index) => (
                <tr key={index}>
                  <td>{item.productId}</td>
                  <td>{item.userId}</td>
                  <td>{item.memberName}</td>
                  <td>{item.partyTypeName}</td>
                  <td>{item.review}</td>
                  <td>{item.rating}</td>
                  <td>{item.partyStartDate}</td>
                  <td>{item.partyLocation}</td>
                  <td>{item.featured ? 'yes' : 'no'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </StyledWrapper>
    </Modal>
  );
};

export default RnrsBulkUploadPreview;
